/*
 * --------------------------------------------------------------------
 * File: \src\utils\WebSocketService.js
 * Created: Monday, 7th October 2025
 * Author: System Consultancy Services Sdn. Bhd.
 * 
 * WebSocket Service - Manages WebSocket connections with auto-reconnect
 * --------------------------------------------------------------------
 */

class WebSocketService {
  constructor() {
    this.ws = null;
    this.url = null;
    this.reconnectInterval = 5000; // 5 seconds
    this.maxReconnectAttempts = 10;
    this.reconnectAttempts = 0;
    this.reconnectTimeout = null;
    this.isIntentionallyClosed = false;
    this.messageHandlers = new Set();
    this.openHandlers = new Set();
    this.closeHandlers = new Set();
    this.errorHandlers = new Set();
  }

  /**
   * Connect to WebSocket server
   * @param {string} url - WebSocket server URL (ws:// or wss://)
   * @param {Object} options - Connection options
   * @param {number} options.reconnectInterval - Time between reconnect attempts (ms)
   * @param {number} options.maxReconnectAttempts - Maximum reconnect attempts
   */
  connect(url, options = {}) {
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      console.log('WebSocket is already connected or connecting');
      return;
    }

    this.url = url;
    this.isIntentionallyClosed = false;
    
    if (options.reconnectInterval) {
      this.reconnectInterval = options.reconnectInterval;
    }
    if (options.maxReconnectAttempts) {
      this.maxReconnectAttempts = options.maxReconnectAttempts;
    }

    this._createConnection();
  }

  _createConnection() {
    try {
      console.log('Connecting to WebSocket:', this.url);
      this.ws = new WebSocket(this.url);

      this.ws.onopen = (event) => {
        console.log('WebSocket connected');
        this.reconnectAttempts = 0;
        this.openHandlers.forEach(handler => handler(event));
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.messageHandlers.forEach(handler => handler(data));
        } catch (error) {
          // If not JSON, pass raw data
          this.messageHandlers.forEach(handler => handler(event.data));
        }
      };

      this.ws.onclose = (event) => {
        console.log('WebSocket closed', event.code, event.reason);
        this.closeHandlers.forEach(handler => handler(event));

        // Attempt to reconnect if not intentionally closed
        if (!this.isIntentionallyClosed && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
          this.reconnectTimeout = setTimeout(() => {
            this._createConnection();
          }, this.reconnectInterval);
        } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
          console.error('Max reconnect attempts reached');
        }
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.errorHandlers.forEach(handler => handler(error));
      };

    } catch (error) {
      console.error('Error creating WebSocket connection:', error);
      this.errorHandlers.forEach(handler => handler(error));
    }
  }

  /**
   * Send data through WebSocket
   * @param {Object|string} data - Data to send
   */
  send(data) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      const message = typeof data === 'string' ? data : JSON.stringify(data);
      this.ws.send(message);
      return true;
    } else {
      console.error('WebSocket is not connected');
      return false;
    }
  }

  /**
   * Close WebSocket connection
   * @param {number} code - Close code
   * @param {string} reason - Close reason
   */
  disconnect(code = 1000, reason = 'Client disconnect') {
    this.isIntentionallyClosed = true;
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    if (this.ws) {
      this.ws.close(code, reason);
      this.ws = null;
    }
  }

  /**
   * Subscribe to message events
   * @param {Function} handler - Message handler function
   * @returns {Function} Unsubscribe function
   */
  onMessage(handler) {
    this.messageHandlers.add(handler);
    return () => this.messageHandlers.delete(handler);
  }

  /**
   * Subscribe to connection open events
   * @param {Function} handler - Open handler function
   * @returns {Function} Unsubscribe function
   */
  onOpen(handler) {
    this.openHandlers.add(handler);
    return () => this.openHandlers.delete(handler);
  }

  /**
   * Subscribe to connection close events
   * @param {Function} handler - Close handler function
   * @returns {Function} Unsubscribe function
   */
  onClose(handler) {
    this.closeHandlers.add(handler);
    return () => this.closeHandlers.delete(handler);
  }

  /**
   * Subscribe to error events
   * @param {Function} handler - Error handler function
   * @returns {Function} Unsubscribe function
   */
  onError(handler) {
    this.errorHandlers.add(handler);
    return () => this.errorHandlers.delete(handler);
  }

  /**
   * Get current connection state
   * @returns {number} WebSocket ready state
   */
  getReadyState() {
    return this.ws ? this.ws.readyState : WebSocket.CLOSED;
  }

  /**
   * Check if WebSocket is connected
   * @returns {boolean}
   */
  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }
}

// Export singleton instance
const websocketService = new WebSocketService();
export default websocketService;
